import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Table,
  Statistic,
  Alert,
  Divider,
  Space,
  Tag,
  Modal,
  message
} from 'antd';
import {
  CalculatorOutlined,
  DollarOutlined,
  BarChartOutlined,
  HistoryOutlined,
  LockOutlined
} from '@ant-design/icons';
import { calculatePerformance } from '../services/adminApi';
import type { CalculationRequest, CalculationResult, UserRole } from '../types';
import { APP_CONFIG } from '../config';

const { Option } = Select;

const CalculationManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [calculationHistory, setCalculationHistory] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    const userStr = localStorage.getItem(APP_CONFIG.userKey);
    if (userStr) {
      const user = JSON.parse(userStr);
      setUserRole(user.role);
      if (user.role !== 'admin') {
        form.setFieldsValue({ role: user.role });
      }
    }
  }, [form]);

  const roleOptions = [
    { value: 'retail', label: '用电用户' },
    { value: 'level1', label: '一级推荐官' },
    { value: 'level2', label: '二级推荐官' },
    { value: 'level3', label: '三级推荐官' },
  ];

  const initialValues: Partial<CalculationRequest> = {
    role: 'level1',
    monthlyUsage: 100,
    retailPrice: 0.8,
    purchaseCost: 0.6,
    directUsers: 10,
    calculationPeriod: 'monthly',
    platformFee: 0.1,
  };

  // 根据用户角色更新初始值
  useEffect(() => {
    const userStr = localStorage.getItem(APP_CONFIG.userKey);
    if (userStr) {
      const user = JSON.parse(userStr);
      setUserRole(user.role);
      if (user.role !== 'admin') {
        form.setFieldsValue({ role: user.role });
      }
    }
  }, [form]);

  const handleCalculate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 如果是管理员，调用后端 API
      if (userRole === 'admin') {
        const response = await calculatePerformance(values as CalculationRequest);

        if (response.data.success) {
          setResult(response.data.data);

          // 保存到历史记录
          const historyItem = {
            id: Date.now(),
            timestamp: new Date().toLocaleString(),
            ...values,
            result: response.data.data,
          };
          setCalculationHistory([historyItem, ...calculationHistory]);

          message.success('计算完成');
        }
      } else {
        // 非管理员，前端计算
        const frontendResult = calculateFrontendPerformance(values);
        setResult(frontendResult);

        // 保存到历史记录
        const historyItem = {
          id: Date.now(),
          timestamp: new Date().toLocaleString(),
          ...values,
          result: frontendResult,
        };
        setCalculationHistory([historyItem, ...calculationHistory]);

        message.success('计算完成（前端计算）');
      }
    } catch (error: any) {
      message.error(error.message || '计算失败');
    } finally {
      setLoading(false);
    }
  };

  // 前端业绩计算逻辑
  const calculateFrontendPerformance = (values: any): CalculationResult => {
    const {
      monthlyUsage,
      retailPrice,
      purchaseCost,
      directUsers = 0,
      platformFee = 0.1,
      calculationPeriod = 'monthly',
      role
    } = values;

    // 使用用户输入的月用电量
    const usage = monthlyUsage;

    // 计算总收入和总成本
    const totalRevenue = usage * retailPrice;
    const totalCost = usage * purchaseCost;
    const basicProfit = totalRevenue - totalCost;

    // 平台费用
    const platformFeeAmount = basicProfit * platformFee;
    const distributableProfit = basicProfit - platformFeeAmount;

    // 分润比例
    let profitRate = 0; // 利润比例

    // 根据角色设置分润比例
    if (role === 'level1') {
      profitRate = 0.8; // 一级推荐官 80%
    } else if (role === 'level2') {
      profitRate = 0.84; // 二级推荐官 80% + 4% = 84%
    } else if (role === 'level3') {
      profitRate = 0.85; // 三级推荐官 80% + 4% + 1% = 85%
    } else {
      profitRate = 0.75; // 用电用户 75%
    }

    // 计算我的收益
    const myEarnings = distributableProfit * profitRate;
    const details: any[] = [];

    // 添加明细
    details.push({
      item: '总收入',
      formula: `${usage}度 × 零售单价 = ${usage} × ${retailPrice} = ¥${totalRevenue.toFixed(2)}`,
      amount: totalRevenue
    });

    details.push({
      item: '总成本',
      formula: `${usage}度 × 采购成本 = ${usage} × ${purchaseCost} = ¥${totalCost.toFixed(2)}`,
      amount: totalCost
    });

    details.push({
      item: '基础利润',
      formula: `总收入 - 总成本 = ¥${totalRevenue.toFixed(2)} - ¥${totalCost.toFixed(2)} = ¥${basicProfit.toFixed(2)}`,
      amount: basicProfit
    });

    details.push({
      item: '平台费用',
      formula: `基础利润 × 平台费率 = ¥${basicProfit.toFixed(2)} × ${(platformFee * 100).toFixed(0)}% = ¥${platformFeeAmount.toFixed(2)}`,
      amount: platformFeeAmount
    });

    details.push({
      item: '可分配利润',
      formula: `基础利润 - 平台费用 = ¥${basicProfit.toFixed(2)} - ¥${platformFeeAmount.toFixed(2)} = ¥${distributableProfit.toFixed(2)}`,
      amount: distributableProfit
    });

    details.push({
      item: '我的收益',
      formula: `可分配利润 × ${(profitRate * 100).toFixed(0)}% = ¥${distributableProfit.toFixed(2)} × ${(profitRate * 100).toFixed(0)}% = ¥${myEarnings.toFixed(2)}`,
      amount: myEarnings
    });

    return {
      totalRevenue,
      totalCost,
      basicProfit,
      distributableProfit,
      myEarnings,
      details
    };
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    setResult(null);
  };

  const handleViewHistory = () => {
    setHistoryVisible(true);
  };

  const detailColumns = [
    {
      title: '项目',
      dataIndex: 'item',
      key: 'item',
      width: 150,
    },
    {
      title: '计算方式',
      dataIndex: 'formula',
      key: 'formula',
      width: 200,
    },
    {
      title: '金额（元）',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      render: (amount: number) => `¥${amount.toLocaleString()}`,
    },
  ];

  const historyColumns = [
    {
      title: '计算时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: UserRole) => {
        const roleOption = roleOptions.find(opt => opt.value === role);
        return roleOption ? roleOption.label : role;
      },
    },
    {
      title: '月用电量',
      dataIndex: 'monthlyUsage',
      key: 'monthlyUsage',
      width: 100,
      render: (usage: number) => `${usage}万度`,
    },
    {
      title: '总收益',
      dataIndex: ['result', 'myEarnings'],
      key: 'myEarnings',
      width: 120,
      render: (earnings: number) => `¥${earnings?.toLocaleString()}`,
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <Button 
          type="link" 
          size="small"
          onClick={() => {
            setResult(record.result);
            form.setFieldsValue(record);
            setHistoryVisible(false);
          }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card title="业绩计算器" className="form-container">
            <Form
              form={form}
              layout="vertical"
              initialValues={initialValues}
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="role"
                    label="选择角色"
                    rules={[{ required: true, message: '请选择角色' }]}
                  >
                    <Select placeholder="请选择角色" size="large">
                      {roleOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="monthlyUsage"
                    label="月用电量（度）"
                    rules={[{ required: true, message: '请输入月用电量' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.1}
                      placeholder="请输入月用电量"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="retailPrice"
                    label="零售电价（元/度）"
                    rules={[{ required: true, message: '请输入零售电价' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      placeholder="请输入零售电价"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="purchaseCost"
                    label="购电成本（元/度）"
                    rules={[{ required: true, message: '请输入购电成本' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      placeholder="请输入购电成本"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.role !== currentValues?.role}>
                {({ getFieldValue }) => {
                  const role = getFieldValue('role');
                  const showPromoterParams = ['level1', 'level2', 'level3'].includes(role);

                  return (
                    <>
                      {showPromoterParams && (
                        <Row gutter={16}>
                          <Col span={24}>
                            <Form.Item
                              name="directUsers"
                              label="直接推荐用户数"
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                placeholder="请输入直接推荐用户数"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                    </>
                  );
                }}
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="calculationPeriod"
                    label="计算周期"
                    rules={[{ required: true, message: '请选择计算周期' }]}
                  >
                    <Select placeholder="请选择计算周期">
                      <Option value="monthly">月度</Option>
                      <Option value="quarterly">季度</Option>
                      <Option value="yearly">年度</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="platformFee"
                    label="平台抽成比例"
                    rules={[{ required: true, message: '请输入平台抽成比例' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={1}
                      step={0.01}
                      placeholder="请输入平台抽成比例"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Space size="large">
                <Button 
                  type="primary" 
                  size="large"
                  icon={<CalculatorOutlined />}
                  loading={loading}
                  onClick={handleCalculate}
                >
                  计算业绩
                </Button>
                <Button 
                  size="large"
                  onClick={handleReset}
                >
                  重置参数
                </Button>
                <Button 
                  size="large"
                  icon={<HistoryOutlined />}
                  onClick={handleViewHistory}
                >
                  计算历史
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {result ? (
            <>
              <Card title="计算结果" style={{ marginBottom: '16px' }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="总收入"
                      value={result.totalRevenue}
                      prefix={<DollarOutlined />}
                      precision={0}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="总成本"
                      value={result.totalCost}
                      prefix={<BarChartOutlined />}
                      precision={0}
                      valueStyle={{ color: '#f5222d' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="基础毛利"
                      value={result.basicProfit}
                      precision={0}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="可分配毛利"
                      value={result.distributableProfit}
                      precision={0}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={24}>
                    <Alert
                      message="我的收益"
                      description={`¥${result.myEarnings.toLocaleString()}`}
                      type="success"
                      showIcon
                      style={{ textAlign: 'center', fontSize: '18px' }}
                    />
                  </Col>
                </Row>

                <Divider />
              </Card>

              <Card title="详细明细">
                <Table
                  columns={detailColumns}
                  dataSource={result!.details}
                  pagination={false}
                  size="small"
                />
              </Card>
            </>
          ) : (
            <Card className="empty-container">
              <div style={{ textAlign: 'center', color: '#999' }}>
                <CalculatorOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div>请设置参数并点击"计算业绩"按钮</div>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="计算历史"
        open={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          columns={historyColumns}
          dataSource={calculationHistory}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Modal>
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card title="业绩计算器" extra={<CalculatorOutlined />}>
            <Form
              form={form}
              layout="vertical"
              initialValues={initialValues}
            >
              <Form.Item
                label="角色类型"
                name="role"
                rules={[{ required: true, message: '请选择角色类型' }]}
              >
                <Select
                  placeholder="请选择角色类型"
                  disabled={userRole !== 'admin'}
                >
                  {roleOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="月度用电量 (度)"
                name="monthlyUsage"
                rules={[{ required: true, message: '请输入月度用电量' }]}
              >
                <InputNumber
                  min={0}
                  max={1000000}
                  style={{ width: '100%' }}
                  placeholder="请输入月度用电量"
                />
              </Form.Item>

              <Form.Item
                label="零售单价 (元/度)"
                name="retailPrice"
                rules={[{ required: true, message: '请输入零售单价' }]}
              >
                <InputNumber
                  min={0}
                  max={10}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="请输入零售单价"
                />
              </Form.Item>

              <Form.Item
                label="采购成本 (元/度)"
                name="purchaseCost"
                rules={[{ required: true, message: '请输入采购成本' }]}
              >
                <InputNumber
                  min={0}
                  max={10}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="请输入采购成本"
                />
              </Form.Item>

              <Form.Item
                label="直推用户数"
                name="directUsers"
                rules={[{ required: true, message: '请输入直推用户数' }]}
              >
                <InputNumber
                  min={0}
                  max={10000}
                  style={{ width: '100%' }}
                  placeholder="请输入直推用户数"
                />
              </Form.Item>

              <Form.Item
                label="计算周期"
                name="calculationPeriod"
                rules={[{ required: true, message: '请选择计算周期' }]}
              >
                <Select placeholder="请选择计算周期">
                  <Option value="daily">日</Option>
                  <Option value="weekly">周</Option>
                  <Option value="monthly">月</Option>
                  <Option value="quarterly">季</Option>
                  <Option value="yearly">年</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="平台费率 (%)"
                name="platformFee"
                rules={[{ required: true, message: '请输入平台费率' }]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.1}
                  style={{ width: '100%' }}
                  placeholder="请输入平台费率"
                />
              </Form.Item>

              <Space style={{ width: '100%', justifyContent: 'center' }}>
                <Button type="primary" onClick={handleCalculate} loading={loading} icon={<CalculatorOutlined />}>
                  计算业绩
                </Button>
                <Button onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          {result ? (
            <>
              <Card title="计算结果" extra={<DollarOutlined />} style={{ marginBottom: '16px' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} lg={6}>
                    <Statistic
                      title="总收入"
                      value={result!.totalRevenue}
                      prefix="¥"
                      precision={2}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Statistic
                      title="总成本"
                      value={result!.totalCost}
                      prefix="¥"
                      precision={2}
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Statistic
                      title="基础利润"
                      value={result!.basicProfit}
                      prefix="¥"
                      precision={2}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Statistic
                      title="可分配利润"
                      value={result!.distributableProfit}
                      prefix="¥"
                      precision={2}
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                  <Col span={24}>
                    <Alert
                      message="我的收益"
                      description={`¥${result!.myEarnings.toLocaleString()}`}
                      type="success"
                      showIcon
                      style={{ textAlign: 'center', fontSize: '18px' }}
                    />
                  </Col>
                </Row>
              </Card>

              <Card title="详细明细">
                <Table
                  columns={detailColumns}
                  dataSource={result?.details}
                  pagination={false}
                  size="small"
                />
              </Card>
            </>
          ) : (
            <Card className="empty-container">
              <div style={{ textAlign: 'center', color: '#999' }}>
                <CalculatorOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div>请设置参数并点击"计算业绩"按钮</div>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="计算历史"
        open={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          columns={historyColumns}
          dataSource={calculationHistory}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Modal>
    </div>
  );
};

export default CalculationManagement;
